import React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import { CONTACT_FIELD_LABELS } from "../../../utils/contactValidation";

const wrapWithLogoPreview = (src, label, child, options = {}) => {
  const { maxWidth = 240, maxHeight = 160, testId } = options;
  const previewLabel = label || "Logo";
  const hasSrc = Boolean(src);
  return (
    <Tooltip
      title={
        hasSrc ? (
          <Box
            component="img"
            src={src}
            alt={`Logo Vorschau ${previewLabel}`}
            data-testid={testId ? `${testId}-preview` : undefined}
            sx={{
              display: "block",
              maxWidth,
              maxHeight,
              objectFit: "contain",
            }}
          />
        ) : (
          <Box sx={{ p: 1.5 }} data-testid={testId ? `${testId}-preview` : undefined}>
            <Typography variant="caption" color="text.secondary">
              Logo Vorschau wird geladen
            </Typography>
          </Box>
        )
      }
      arrow
      placement="top"
    >
      <Box component="span" sx={{ display: "inline-flex" }} data-testid={testId} data-has-preview={hasSrc ? "true" : "false"}>
        {child}
      </Box>
    </Tooltip>
  );
};

const StatusChips = ({ profileData }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap">
    <Chip
      label={profileData.kleinunternehmer ? "Kleinunternehmer (§ 19 UStG)" : "Regelbesteuerung"}
      color={profileData.kleinunternehmer ? "warning" : "success"}
      variant="outlined"
      size="small"
    />
    {!profileData.kleinunternehmer && (
      <Chip
        label={profileData.istVersteuerung ? "Ist-Versteuerung" : "Soll-Versteuerung"}
        color={profileData.istVersteuerung ? "info" : "default"}
        variant="outlined"
        size="small"
      />
    )}
  </Stack>
);

export default function ProfileCompanyCard({
  open,
  onToggle,
  profileData,
  displayedLogos,
  logoPreviews,
  logoLoading,
  logoUploading,
  uploadAsPrimary,
  onUploadAsPrimaryChange,
  onLogoUpload,
  onSetPrimaryLogo,
  onDeleteLogo,
  fieldErrors,
  emailError,
  phoneError,
  onInputChange,
}) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: "0 8px 28px rgba(0,0,0,0.06)",
        mb: 3,
      }}
    >
      <CardHeader
        title="Unternehmensprofil"
        subheader="Name, Kontakt und Branding"
        action={onToggle(<StatusChips profileData={profileData} />)}
      />
      <Collapse in={open} timeout="auto">
        <Divider />
        <CardContent>
          <Stack direction="column" spacing={3} alignItems="flex-start">
            <Box sx={{ width: "100%", order: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Logos
              </Typography>
              <Stack spacing={1}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadRoundedIcon />}
                    sx={{
                      bgcolor: "orange",
                      "&:hover": { bgcolor: "#cc7a00" },
                      borderRadius: 2,
                    }}
                    disabled={logoUploading}
                  >
                    Logo hochladen
                    <input type="file" hidden accept="image/*" onChange={(event) => onLogoUpload(event.target.files?.[0])} />
                  </Button>
                  <FormControlLabel
                    control={<Checkbox checked={uploadAsPrimary} onChange={(event) => onUploadAsPrimaryChange(event.target.checked)} disabled={logoUploading} />}
                    label="Als Standard setzen"
                  />
                  {logoUploading && <Chip label="Upload" size="small" />}
                </Stack>
                {logoLoading && <LinearProgress />}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Empfohlen: PNG/JPEG, min. 300x300 px, max. 5 MB
                </Typography>
              </Stack>

              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {displayedLogos.length === 0 && !logoLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Noch kein Logo hinterlegt.
                  </Typography>
                ) : (
                  <Stack data-testid="profil-logo-list" direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                    {displayedLogos.map((logo) => (
                      <Card
                        key={logo.id}
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          width: { xs: "100%", sm: 320 },
                          flexGrow: 0,
                          flexShrink: 0,
                        }}
                      >
                        <CardContent sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                          {wrapWithLogoPreview(
                            logoPreviews[logo.id],
                            logo.fileName || "Logo",
                            (
                              <Avatar
                                variant="rounded"
                                src={logoPreviews[logo.id] || undefined}
                                sx={{ width: 56, height: 56, borderRadius: 2 }}
                              >
                                {!logoPreviews[logo.id] ? "Logo" : null}
                              </Avatar>
                            ),
                            { testId: `profil-logo-avatar-${logo.id}` }
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap title={logo.fileName || "Logo"}>
                              {logo.fileName || "Logo"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {Math.max(1, Math.round((logo.size || 0) / 1024))} KB
                            </Typography>
                          </Box>
                          {logo.primaryLogo && <Chip label="Standard" color="success" size="small" />}
                        </CardContent>
                        <CardActions sx={{ justifyContent: "space-between", pt: 0, pb: 1.5, px: 2 }}>
                          {!logo.primaryLogo ? (
                            <Button size="small" onClick={() => onSetPrimaryLogo(logo.id)} disabled={logoUploading}>
                              Als Standard
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Aktives Logo
                            </Typography>
                          )}
                          <Button
                            size="small"
                            color="inherit"
                            startIcon={<DeleteOutlineRoundedIcon />}
                            onClick={() => onDeleteLogo(logo.id)}
                            disabled={logoUploading}
                          >
                            Löschen
                          </Button>
                        </CardActions>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Box>

            <Box sx={{ flex: 1, width: "100%", order: 1 }}>
              <Stack spacing={2}>
                <TextField
                  label="Unternehmensname"
                  value={profileData.companyName || ""}
                  onChange={(event) => onInputChange("companyName", event.target.value)}
                  required
                  fullWidth
                  error={Boolean(fieldErrors.companyName)}
                  helperText={fieldErrors.companyName || ""}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label={CONTACT_FIELD_LABELS.email}
                    type="email"
                    value={profileData.email || ""}
                    onChange={(event) => onInputChange("email", event.target.value)}
                    error={Boolean(fieldErrors.email || emailError)}
                    helperText={fieldErrors.email || emailError || ""}
                    required
                    fullWidth
                  />
                  <TextField
                    label={CONTACT_FIELD_LABELS.phone}
                    value={profileData.phone || ""}
                    onChange={(event) => onInputChange("phone", event.target.value)}
                    error={Boolean(fieldErrors.phone || phoneError)}
                    helperText={fieldErrors.phone || phoneError || ""}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="PayPal-Adresse"
                  value={profileData.paypalAddress || ""}
                  onChange={(event) => onInputChange("paypalAddress", event.target.value)}
                  error={Boolean(fieldErrors.paypalAddress)}
                  helperText={fieldErrors.paypalAddress || ""}
                  fullWidth
                />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
}
